<script>
	import { mapState, mapGetters } from "vuex";
	import BaseDropdownSubmenu from "../UI/BaseDropdownSubmenu";
	import i18n from "@/i18n";

	export default {
		components: {
			BaseDropdownSubmenu
		},
		props: {
			sectionId: {
				type: Number,
				required: true
			},
			index: {
				type: Number,
				required: true
			},
			noAccounts: {
				type: Boolean,
				required: false,
				default: false
			},
			accountId: {
				type: [Number, String],
				required: false,
				default: null
			}
		},
		data() {
			return {
				disabled: true,
				isOpen: false,
				selectedAccount: null
			}
		},
		computed: {
			...mapGetters(["searchAllRows"]),
			...mapState({
				table: state => state.billing.table,
				accounts: state => state.billing.accounts,
				technicalName: state => state.settings.technicalName,
				headers: state => state.billing.headers
			})
		},
		watch: {
			accountId: {
				handler() {
					this.selectAccount();
				},
			},
			accounts: {
				handler() {
					this.selectAccount();
				},
				immediate: true
			},
			selectedAccount: {
				handler() {
					this.$emit("update:accountId", this.selectedAccount?.id);
				},
				immediate: true,
				deep: true
			}
		},
		methods: {
			/**
			 * request the parent for a new line of specified component at the specified index
			 * @param {String} component name of the component to be added
			 * @param {Number} position index to add the new component at
			 * @param {Event} event native event firing the method
			 */
			append(component, position, event) {
				// toggle out the dropdown, the event is mendatory
				// see : https://github.com/bootstrap-vue/bootstrap-vue/blob/869d2f07b688d039b685741cacc73e6e6b5dc6fa/src/mixins/dropdown.js#L313
				this.$refs.dropdown.toggle(event);

				this.$emit("event-append", { component, position });
			},
			/**
			 * request the parent for component to be removed at the specified index
			 * @param {String} sectionId parent section's id of the component to be removed
			 * @param {Number} index index of the component to be removed
			 */
			remove(sectionId, index) {
				// If there is only one section and only one entry in this section, don't allow the removable of the entry
				if (
					this.table.sections.length === 1 &&
					this.searchAllRows("entry").length === 1 &&
					this.noAccounts !== true
				) {
					return confirm(i18n.t("Vous ne pouvez pas supprimer cette entrée."));
				}

				this.$emit("event-remove", { sectionId, index });
			},
			/**
			 * request the parent for this component to be duplicated
			 * @param {Number} position index to add the new component at
			 * @param {Event} event native event firing the method
			 */
			duplicate(position, event) {
				// toggle out the dropdown, the event is mendatory
				// see : https://github.com/bootstrap-vue/bootstrap-vue/blob/869d2f07b688d039b685741cacc73e6e6b5dc6fa/src/mixins/dropdown.js#L313
				this.$refs.dropdown.toggle(event);

				this.$emit("event-duplicate", position);
			},
			/**
			 * Force the display of the dropdown
			 */
			show() {
				this.disabled = false;

				this.$nextTick(() => {
					if (this.$refs.dropdown) {
						this.$refs.dropdown.show();
					}
				});
			},
			onHide() {
				this.disabled = true;
				this.isOpen = false;
			},
			onShow() {
				this.isOpen = true;
			},
			selectAccount() {
				if (this.accountId) {
					this.selectedAccount = this.accounts.find(account => account.id === Number(this.accountId));
				}

				if (this.selectedAccount === null) {
					this.selectedAccount = this.accounts.find(account => {
						if (account.technical_name === this.technicalName) {
							return account;
						}
					});
				}
			},
			reformulateWithAI(position, event) {
				this.$refs.dropdown.toggle(event);
				this.$emit("event-reformulateWithAI", position);
			},
			smartCorrector(position, event) {
				this.$refs.dropdown.toggle(event);
				this.$emit("event-smartCorrector", position);
			},
		}
	};
</script>

<template>
	<div tabindex="-1" :class="{ handle: !isOpen }" @click="show">
		<b-dropdown
			:id="`dropdown_${sectionId}_${index}`"
			ref="dropdown"
			variant="link"
			toggle-class="text-decoration-none"
			no-caret
			class="sinao-dropdown-body"
			:disabled="disabled"
			@hide="onHide"
			@show="onShow"
			:style="{'right': headers.line_number.enabled ? '46px' : '359px'}"
		>
			<template #button-content>
				<svg
					xmlns:svg="http://www.w3.org/2000/svg"
					xmlns="http://www.w3.org/2000/svg"
					version="1.1"
					class="bi-three-dots-vertical b-icon bi"
					fill="var(--link)"
					alt="icon"
					role="img"
					focusable="false"
					height="1em"
					width="1em"
					viewBox="-5 -5 23 23"
				>
					<g transform="translate(-2, -8)">
						<path
							id="path30"
							clip-rule="evenodd"
							d="M7.646.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 1.707V5.5a.5.5 0 0 1-1 0V1.707L6.354 2.854a.5.5 0 1 1-.708-.708l2-2zM8 10a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 14.293V10.5A.5.5 0 0 1 8 10zM.146 8.354a.5.5 0 0 1 0-.708l2-2a.5.5 0 1 1 .708.708L1.707 7.5H5.5a.5.5 0 0 1 0 1H1.707l1.147 1.146a.5.5 0 0 1-.708.708l-2-2zM10 8a.5.5 0 0 1 .5-.5h3.793l-1.147-1.146a.5.5 0 0 1 .708-.708l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L14.293 8.5H10.5A.5.5 0 0 1 10 8z"
							fill-rule="evenodd"
						/>
					</g>
					<g transform="translate(15, -8)">
						<path
							id="path30-5"
							fill-rule="evenodd"
							d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"
							clip-rule="evenodd"
						/>
					</g>
				</svg>

				<b-tooltip
					placement="left"
					:target="`dropdown_${sectionId}_${index}`"
					container="body"
					custom-class="smallTextToolTip"
				>	
					<table style="border: 0">
						<tr>
							<td tyle="vertical-align: top">
								<b-icon-arrows-move font-scale="1.2" />
							</td>
							<td style="text-align: left">
								{{ $t("Déplacer") }}
							</td>
						</tr>
						<tr>
							<td style="vertical-align: top">
								<b-icon-pencil font-scale="1.2" />
							</td>
							<td style="text-align: left">
								{{ $t("Editer") }}
							</td>
						</tr>
					</table>
				</b-tooltip>
			</template>

			<b-dropdown-item-button @click="smartCorrector(index + 1, $event)">
				<div class="containerButtonIa" style="margin-left: -10px;">
					<div class="buttonIA">{{ $t("IA") }}</div>
					{{ $t("Corriger l’orthographe") }}
				</div>
			</b-dropdown-item-button>

			<b-dropdown-item-button @click="reformulateWithAI(index + 1, $event)">
				<div class="containerButtonIa" style="margin-left: -10px;">
					<div class="buttonIA">{{ $t("IA") }}</div>
					{{ $t("Reformuler") }}
				</div>
			</b-dropdown-item-button>

			<!-- <b-dropdown-divider /> -->

			<b-dropdown-item-button @click="remove(sectionId, index)">
				{{ $t("Supprimer") }}
			</b-dropdown-item-button>
			<b-dropdown-item-button @click="duplicate(index + 1, $event)">
				{{ $t("Dupliquer") }}
			</b-dropdown-item-button>

			<!-- <b-dropdown-divider /> -->

			<BaseDropdownSubmenu>
				<template #button-content>
					{{ $t("Ajouter au dessus") }}
				</template>

				<b-dropdown-item-button class="dropdown-item-with-icon" @click="append('entry', index, $event)">
					<b-icon font-scale="1.3" icon="plus" />
					<span>{{ $t("Ligne") }}</span>
				</b-dropdown-item-button>
				<b-dropdown-item-button class="dropdown-item-with-icon" @click="append('comment', index, $event)">
					<b-icon font-scale="1.3" icon="card-text" />
					<span>{{ $t("Commentaire") }}</span>
				</b-dropdown-item-button>
			</BaseDropdownSubmenu>

			<BaseDropdownSubmenu>
				<template #button-content>
					{{ $t("Ajouter au dessous") }}
				</template>

				<b-dropdown-item-button class="dropdown-item-with-icon" @click="append('entry', index + 2, $event)">
					<b-icon font-scale="1.3" icon="plus" />
					<span>{{ $t("Ligne") }}</span>
				</b-dropdown-item-button>
				<b-dropdown-item-button class="dropdown-item-with-icon" @click="append('comment', index + 2, $event)">
					<b-icon font-scale="1.3" icon="card-text" />
					<span>{{ $t("Commentaire") }}</span>
				</b-dropdown-item-button>
			</BaseDropdownSubmenu>
			
			<b-dropdown-form v-if="noAccounts === false" class="mt-2" style="background-color: var(--background)">
				<label class="mb-1 text-center" style="font-size: 13px">
					{{ $t("Compte comptable") }}
				</label>
				<b-form-select v-model="selectedAccount" size="sm" class="dropdown-account billing__input billing__input--solid">
					<b-form-select-option v-for="(account, index2) in accounts" :key="index2" :value="account">
						{{ account.accounting_number }} - {{ account.name }}
          			</b-form-select-option>
				</b-form-select>
			</b-dropdown-form>
		</b-dropdown>
	</div>
</template>

<style scoped lang="scss">
	@import "../../scss/variables.scss";

	.dropdown {
		&-divider {
			margin: 0;
		}

		&.sinao-dropdown-body {
			position: absolute;
			font-size: 14px;
			right: 359px;
			height: 25px;
			top: 6px;
			width: 25px;
			color: var(--link) !important;

			::v-deep {
				& > button {
					padding: 8px 1px;
					border: none;
					display: flex;

					svg {
						padding: 0;
						font-size: 1em;
						@media screen and (prefers-reduced-motion: no-preference) {
							transition: color 300ms ease-in-out;
						}
					}
				}
				.dropdown {
					&-item {
						padding: 0.25em 0.25em 0.25em 1.7em;
						font-size: 14px;
						width: 100%;
					}
					&-divider {
						padding: 0.25em 0;
					}
					&-menu {
						padding: 0 0;
						min-width: 195px;
						border: none;
						box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
						border-radius: 2px;
					}
				}
				ul {
					padding: 0;
					width: 100%;

					& * {
						font-size: 14px;
					}
				}
			}
			header {
				padding: 2px 0 2px 15px;
			}
			form {
				padding: 0.25rem 0.5rem;
			}

			&:hover {
				color: var(--link-hover) !important;
			}
		}

		&-item-with-icon ::v-deep &-item {
			display: flex;
			align-items: center;

			svg {
				margin: 0 5px 0 -10px;
			}
		}
	}
	.handle:active,
	.handle :active,
	.handle:active *,
	.handle :active * {
		cursor: grabbing;
	}
	.handle {
		cursor: move;
	}

	.buttonIA {
		background-color: rgb(161, 131, 191);
		color: var(--white);
		padding: 2px 4px;
		font-size: 10px !important;
		border-radius: 2px;
		margin-right: 6px;
	}

	.containerButtonIa {
		display: flex;
		// justify-content: space-between;
		align-items: center;
	}

	.dropdown-account {
		display: flex;
		flex-direction: column;
		align-items: start;
	}
</style>
